import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  getOperationCoordinator,
} from "./operationCoordinator";
import {
  STEP_STATUS,
  OPERATION_PHASE,
  isConfirmedStepSuccess,
} from "./types";
import { TechnicalErrorDetails } from "./TechnicalErrorDetails";

function StepIcon({ status }) {
  if (isConfirmedStepSuccess(status)) {
    return (
      <CheckCircleIcon
        className="h-5 w-5 text-green-600 flex-shrink-0"
        aria-hidden="true"
      />
    );
  }
  if (status === STEP_STATUS.FAILED || status === STEP_STATUS.ROLLED_BACK) {
    return (
      <XCircleIcon
        className="h-5 w-5 text-red-600 flex-shrink-0"
        aria-hidden="true"
      />
    );
  }
  if (
    status === STEP_STATUS.NOT_RELEVANT ||
    status === STEP_STATUS.NOT_DONE
  ) {
    return (
      <MinusCircleIcon
        className="h-5 w-5 text-slate-400 flex-shrink-0"
        aria-hidden="true"
      />
    );
  }
  if (status === STEP_STATUS.RUNNING) {
    return (
      <ArrowPathIcon
        className="h-5 w-5 text-blue-600 flex-shrink-0 animate-spin"
        aria-hidden="true"
      />
    );
  }
  // pending / needs_fix / unconfirmed / cancelled
  return (
    <span
      className="h-5 w-5 flex-shrink-0 rounded-full border-2 border-slate-300"
      aria-hidden="true"
    />
  );
}

function statusLabel(t, status) {
  const key = `operation_feedback.step.${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

/**
 * Shared action / progress / result dialog.
 * Hide/Escape does not cancel server work — use hide(), not dismiss(), for that.
 */
export function OperationActionDialog({
  clientOperationId,
  onClose,
  primaryAction,
  secondaryAction,
}) {
  const { t } = useTranslation();
  const coordinator = getOperationCoordinator();
  const [snapshot, setSnapshot] = useState(() => coordinator.getSnapshot());

  useEffect(() => {
    return coordinator.subscribe(setSnapshot);
  }, [coordinator]);

  const op =
    snapshot.operations.find((o) => o.clientOperationId === clientOperationId) ||
    coordinator.getOperation(clientOperationId);

  const open = Boolean(op?.visible);
  const isResult = op?.phase === OPERATION_PHASE.RESULT;

  const handleHide = () => {
    if (clientOperationId) coordinator.hide(clientOperationId);
    if (onClose) onClose({ reason: "hide" });
  };

  const handleDismiss = () => {
    if (clientOperationId) coordinator.dismiss(clientOperationId);
    if (onClose) onClose({ reason: "dismiss" });
  };

  if (!op) return null;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[2100]"
        onClose={handleHide}
        aria-labelledby="operation-action-dialog-title"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-700/50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-0 text-center sm:items-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform overflow-hidden rounded-none sm:rounded-2xl bg-white p-4 sm:p-6 text-left shadow-xl transition-all">
                <Dialog.Title
                  id="operation-action-dialog-title"
                  className="text-lg font-semibold text-slate-900"
                >
                  {op.title}
                </Dialog.Title>

                <div
                  className="mt-4 space-y-3"
                  role="status"
                  aria-live="polite"
                  aria-atomic="false"
                >
                  {op.steps.map((step) => (
                    <div
                      key={step.key}
                      className="flex items-start gap-3 text-sm text-slate-700"
                    >
                      <StepIcon status={step.status} />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900">
                          {step.label}
                        </div>
                        <div className="text-slate-500">
                          {statusLabel(t, step.status)}
                          {step.detail ? ` — ${step.detail}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}

                  {!isResult && (
                    <p className="text-sm text-slate-600 pt-2">
                      {t("operation_feedback.processing")}
                    </p>
                  )}

                  {isResult && op.summary && (
                    <p className="text-sm text-slate-800 pt-2 border-t border-slate-100">
                      {op.summary}
                    </p>
                  )}

                  {isResult &&
                    (op.result?.requestId ||
                      op.result?.error?.requestId) && (
                      <TechnicalErrorDetails
                        requestId={
                          op.result?.requestId ||
                          op.result?.error?.requestId
                        }
                        safeMessage={
                          op.summary ||
                          op.result?.error?.safeMessage ||
                          undefined
                        }
                      />
                    )}

                  {isResult && op.result?.links?.length > 0 && (
                    <ul className="text-sm space-y-1 pt-2">
                      {op.result.links.map((link) => (
                        <li key={link.href || link.label}>
                          <a
                            href={link.href}
                            className="text-blue-700 underline"
                          >
                            {link.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  {!isResult && (
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      onClick={handleHide}
                    >
                      {t("operation_feedback.hide")}
                    </button>
                  )}
                  {isResult && secondaryAction && (
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      onClick={secondaryAction.onClick}
                    >
                      {secondaryAction.label}
                    </button>
                  )}
                  {isResult && (
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                      onClick={
                        primaryAction?.onClick
                          ? primaryAction.onClick
                          : handleDismiss
                      }
                    >
                      {primaryAction?.label || t("operation_feedback.close")}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

/**
 * Host that renders the active dialog-owned operation from the coordinator.
 */
export function OperationFeedbackHost() {
  const [snap, setSnap] = useState(() =>
    getOperationCoordinator().getSnapshot()
  );
  useEffect(() => {
    return getOperationCoordinator().subscribe(setSnap);
  }, []);

  const id = snap.activeDialogId;
  if (!id) return null;
  return <OperationActionDialog clientOperationId={id} />;
}

export default OperationActionDialog;
